import React, { useCallback, useEffect, useState } from 'react';
import {
  MdClose,
  MdManageAccounts,
  MdMemory,
  MdCreditCard,
  MdMap,
  MdExtension,
  MdChevronRight,
  MdArrowBack,
} from 'react-icons/md';
import UsageBillingModal from './UsageBillingModal';
import DataCoveragePanel from './DataCoveragePanel';
import CompanySetupForm from './CompanySetupForm';
import BuilderProfileModal from './BuilderProfileModal';
import EmailConnectionCard from './EmailConnectionCard';
import ConnectedAppsList from './ConnectedAppsList';
import './SettingsModal.css';

const SECTIONS = [
  { id: 'user', label: 'User Info & Memories', icon: MdManageAccounts },
  { id: 'atlas', label: 'Atlas Settings', icon: MdMemory },
  { id: 'connectors', label: 'Connectors', icon: MdExtension },
  { id: 'billing', label: 'Billing & Usage', icon: MdCreditCard },
  { id: 'coverage', label: 'Coverage', icon: MdMap },
];

function isPrivateIpv4(hostname) {
  const parts = hostname.split('.');
  if (parts.length !== 4 || parts.some((part) => !/^\d+$/.test(part))) return false;
  const octets = parts.map(Number);
  if (octets.some((part) => part < 0 || part > 255)) return true;
  const [first, second] = octets;
  return first === 0
    || first === 10
    || first === 127
    || (first === 100 && second >= 64 && second <= 127)
    || (first === 169 && second === 254)
    || (first === 172 && second >= 16 && second <= 31)
    || (first === 192 && second === 168)
    || (first === 198 && (second === 18 || second === 19))
    || first >= 224;
}

function isLocalMcpHostname(rawHostname) {
  const hostname = String(rawHostname || '').replace(/^\[|\]$/g, '').replace(/\.$/, '').toLowerCase();
  if (hostname === 'localhost' || hostname.endsWith('.localhost') || hostname.endsWith('.local')) return true;
  if (isPrivateIpv4(hostname)) return true;
  return hostname === '::'
    || hostname === '::1'
    || /^f[cd]/.test(hostname)
    || /^fe[89ab]/.test(hostname);
}

function AtlasSettingsPanel({ preferences, saving, onUpdate }) {
  const rows = [
    ['start_atlas_in_company_mode', 'Start Atlas in company mode', 'Open in your company workspace when Atlas loads.'],
    ['prompt_on_new_company_chat', 'Prompt before new company chats', 'Confirm before content becomes available to your company.'],
    ['use_personal_memories_in_company_mode', 'Use personal memories in company mode', 'Allow personal memories to help while working in the shared workspace.'],
  ];
  return (
    <div className="settings-page">
      <div className="settings-page-heading"><h2>Atlas Settings</h2><p>Your previous User Settings, consolidated here.</p></div>
      <section className="settings-card settings-toggle-list">
        {rows.map(([key, label, hint]) => (
          <label className="settings-toggle-row" key={key}>
            <span><strong>{label}</strong><small>{hint}</small></span>
            <input type="checkbox" role="switch" checked={Boolean(preferences[key])}
                   disabled={saving || !preferences.persistence_available}
                   onChange={(event) => onUpdate({ [key]: event.target.checked })} />
          </label>
        ))}
      </section>
      {!preferences.persistence_available && <div className="settings-warning">Settings persistence needs its SQL migration.</div>}
    </div>
  );
}

function ConnectorsPanel({ connector, onOpenMcp }) {
  return (
    <div className="settings-page">
      <div className="settings-page-heading">
        <h2>Connectors</h2>
        <p>Connect Atlas to tools and services you use.</p>
      </div>
      <div className="settings-connector-email">
        <EmailConnectionCard />
      </div>
      <ConnectedAppsList />
      <button type="button" className="settings-connector-row" onClick={onOpenMcp}>
        <span className="settings-connector-icon"><MdExtension size={21} /></span>
        <span className="settings-connector-row-copy">
          <strong>MCP server</strong>
          <small>Connect a remote Model Context Protocol server.</small>
        </span>
        <span className={`settings-connector-status ${connector?.enabled ? 'enabled' : ''}`}>
          {connector?.enabled ? 'On' : 'Off'}
        </span>
        <MdChevronRight className="settings-connector-chevron" size={21} />
      </button>
    </div>
  );
}

function McpConnectorPanel({ connector, saving, onSave, onBack }) {
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setUrl(String(connector?.url || ''));
    setEnabled(Boolean(connector?.enabled));
    setError('');
    setSaved(false);
  }, [connector]);

  const handleSave = async (event) => {
    event.preventDefault();
    const normalizedUrl = url.trim();
    if (enabled && !normalizedUrl) {
      setError('Enter an MCP server URL before enabling the connector.');
      return;
    }
    if (normalizedUrl) {
      try {
        const parsed = new URL(normalizedUrl);
        if (parsed.protocol !== 'https:' || parsed.username || parsed.password || isLocalMcpHostname(parsed.hostname)) {
          throw new Error();
        }
      } catch {
        setError('Enter a valid public HTTPS URL.');
        return;
      }
    }
    setError('');
    setSaved(false);
    try {
      await onSave({ url: normalizedUrl, enabled: enabled && Boolean(normalizedUrl) });
      setSaved(true);
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Could not save this connector.');
    }
  };

  return (
    <div className="settings-page">
      <button type="button" className="settings-detail-back" onClick={onBack} aria-label="Back to connectors">
        <MdArrowBack size={18} /> Connectors
      </button>
      <div className="settings-page-heading">
        <h2>MCP server</h2>
        <p>Connect a remote Model Context Protocol server to Atlas.</p>
      </div>
      <form className="settings-card settings-connector-card" onSubmit={handleSave}>
        <div className="settings-connector-heading">
          <div className="settings-connector-icon"><MdExtension size={21} /></div>
          <div><strong>Remote MCP server</strong><small>Advanced use case · public HTTPS only</small></div>
        </div>
        <div className="settings-connector-warning" role="note">
          <strong>Only use connectors from developers you trust.</strong>
          <span>
            This is an advanced use case for connecting an MCP server to Atlas. Real Torch does not control which
            tools developers make available and cannot verify that they will work as intended or that they won’t change.
          </span>
        </div>
        <label className="settings-field">
          <span>MCP server URL</span>
          <input
            type="url"
            inputMode="url"
            value={url}
            onChange={(event) => { setUrl(event.target.value); setSaved(false); }}
            placeholder="https://example.com/mcp"
            disabled={saving}
            autoComplete="off"
          />
        </label>
        <label className="settings-toggle-row settings-connector-toggle">
          <span><strong>Use this MCP server</strong><small>Make its tools available to Atlas for new messages.</small></span>
          <input type="checkbox" role="switch" checked={enabled} disabled={saving}
                 onChange={(event) => { setEnabled(event.target.checked); setSaved(false); }} />
        </label>
        {error && <div className="settings-warning">{error}</div>}
        {saved && <div className="settings-success">Connector saved.</div>}
        {!connector?.persistence_available && (
          <div className="settings-local-note">Database persistence is not installed, so this local build saves the connector in this browser.</div>
        )}
        <div className="settings-form-actions">
          <button type="submit" className="settings-primary-button" disabled={saving}>
            {saving ? 'Saving…' : 'Save connector'}
          </button>
        </div>
      </form>
    </div>
  );
}

function CoveragePanel({ coverageState, isLoading, expectedUserId, selectedZip, onSelectZip, onCoverageUpdate, onCompanySetupComplete, onSeatRequest }) {
  const resolvedId = coverageState?.data_user_id != null ? String(coverageState.data_user_id) : null;
  const contextLoading = isLoading || (expectedUserId && resolvedId !== String(expectedUserId));
  const needsSetup = coverageState && !coverageState.has_company_membership
    && String(coverageState.actor_user_id || '') === String(coverageState.data_user_id || '');
  return (
    <div className="settings-page settings-coverage-page">
      <div className="settings-page-heading"><h2>Coverage</h2><p>Select ZIP and team changes, then send a request for RealTorch to review.</p></div>
      {contextLoading ? <div className="settings-empty">Loading coverage…</div> : needsSetup ? (
        <CompanySetupForm user={coverageState?.user} pendingJoinRequest={coverageState?.pending_join_request}
                          onComplete={onCompanySetupComplete} submitLabel="Continue to coverage" />
      ) : (
        <DataCoveragePanel coverageState={coverageState} selectedZip={selectedZip} onSelectZip={onSelectZip}
                           onCoverageUpdate={onCoverageUpdate} onCompanySetupComplete={onCompanySetupComplete}
                           onSeatRequest={onSeatRequest} />
      )}
    </div>
  );
}

export default function SettingsModal({
  isOpen,
  onClose,
  initialSection = 'user',
  user,
  selectedClient,
  clients,
  isAdmin,
  workspaceContext,
  atlasUserPreferences,
  atlasUserPreferencesSaving,
  onUpdateAtlasUserPreferences,
  atlasMcpConnector,
  atlasMcpConnectorSaving,
  onUpdateAtlasMcpConnector,
  coverageState,
  coverageLoading,
  expectedCoverageUserId,
  selectedZip,
  onSelectZip,
  onCoverageUpdate,
  onCompanySetupComplete,
  onSeatRequest,
  onPrepareCoverage,
}) {
  const [section, setSection] = useState(initialSection);
  const [connectorView, setConnectorView] = useState('list');

  useEffect(() => {
    if (isOpen) {
      setSection(initialSection);
      setConnectorView('list');
    }
  }, [initialSection, isOpen]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="settings-backdrop" onClick={onClose}>
      <div className="settings-modal" role="dialog" aria-modal="true" aria-label="Settings" onClick={(event) => event.stopPropagation()}>
        <aside className="settings-nav">
          <div className="settings-nav-brand">Settings</div>
          <nav>
            {SECTIONS.map((item) => {
              const Icon = item.icon;
              return (
                <button type="button" key={item.id} className={section === item.id ? 'active' : ''}
                        aria-label={item.label} title={item.label} onClick={() => {
                  setSection(item.id);
                  if (item.id === 'connectors') setConnectorView('list');
                  if (item.id === 'coverage') onPrepareCoverage?.();
                }}>
                  <Icon size={18} /><span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>
        <main className="settings-content">
          <button type="button" className="settings-close" onClick={onClose} aria-label="Close settings"><MdClose size={21} /></button>
          {section === 'user' && (
            <div className="settings-page settings-profile-page">
              <BuilderProfileModal
                isOpen
                embedded
                onClose={onClose}
                user={user}
                selectedClient={selectedClient}
                clients={clients}
                isAdmin={isAdmin}
                workspaceContext={workspaceContext}
              />
            </div>
          )}
          {section === 'atlas' && <AtlasSettingsPanel preferences={atlasUserPreferences} saving={atlasUserPreferencesSaving} onUpdate={onUpdateAtlasUserPreferences} />}
          {section === 'connectors' && connectorView === 'list' && (
            <ConnectorsPanel connector={atlasMcpConnector} onOpenMcp={() => setConnectorView('mcp')} />
          )}
          {section === 'connectors' && connectorView === 'mcp' && (
            <McpConnectorPanel
              connector={atlasMcpConnector}
              saving={atlasMcpConnectorSaving}
              onSave={onUpdateAtlasMcpConnector}
              onBack={() => setConnectorView('list')}
            />
          )}
          {section === 'billing' && (
            <div className="settings-page">
              <div className="settings-page-heading"><h2>Billing &amp; Usage</h2><p>Understand daily Atlas spend, team usage, credits, and billing controls.</p></div>
              <UsageBillingModal
                embedded
                ownerUserId={isAdmin ? selectedClient : null}
              />
            </div>
          )}
          {section === 'coverage' && (
            <CoveragePanel coverageState={coverageState} isLoading={coverageLoading} expectedUserId={expectedCoverageUserId}
                           selectedZip={selectedZip} onSelectZip={onSelectZip} onCoverageUpdate={onCoverageUpdate}
                           onCompanySetupComplete={onCompanySetupComplete} onSeatRequest={onSeatRequest} />
          )}
        </main>
      </div>
    </div>
  );
}
